import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { customerOrdersTourId, customerOrdersSteps } from '@/tours/customer';
import { Package } from 'lucide-react';
import { ordersApi } from '@/services/api/ordersApi';
import { queryKeys } from '@/services/api/queryClient';
import { OrderStatus } from '@/types/enums';
import type { OrderStatus as OrderStatusType } from '@/types/enums';
import { EmptyState, LoadingSpinner, ErrorMessage, SearchInput, Pagination } from '@/components/ui';
import OrderCard from '@/components/features/customer/OrderCard';
import { cn } from '@/lib/utils';

const TABS = [
  { key: 'all', label: 'Все' },
  { key: 'active', label: 'Активные' },
  { key: 'done', label: 'Завершённые' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const COMPLETED_STATUSES = new Set<OrderStatusType>([
  OrderStatus.COMPLETED,
]);

const ITEMS_PER_PAGE = 10;

const MyOrdersPage: React.FC = () => {
  useTourAutoStart({ tourId: customerOrdersTourId, steps: customerOrdersSteps });
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [page, setPage] = useState(1);

  const filtersKey = JSON.stringify({ search, activeTab, page });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: queryKeys.myOrderList(filtersKey),
    queryFn: () => ordersApi.getMyOrders({ search: search || undefined, page, limit: ITEMS_PER_PAGE }),
  });

  const handleSearch = useCallback((val: string) => {
    setSearch(val);
    setPage(1);
  }, []);

  const handleTabChange = useCallback((tab: TabKey) => {
    setActiveTab(tab);
    setPage(1);
  }, []);

  const allOrders = data?.data ?? [];

  const filteredOrders = activeTab === 'all'
    ? allOrders
    : activeTab === 'active'
      ? allOrders.filter(o => !COMPLETED_STATUSES.has(o.status))
      : allOrders.filter(o => COMPLETED_STATUSES.has(o.status));

  const totalPages = data ? Math.ceil(data.count / ITEMS_PER_PAGE) : 1;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Мои заказы</h1>
        <p className="text-sm text-gray-500 mt-1">Отслеживание ваших заказов</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Поиск по номеру заказа…"
          className="flex-1"
        />

        <div data-tour="customer:orders-tabs" className="flex rounded-xl bg-gray-100 p-1 shrink-0">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="py-20">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {isError && (
        <ErrorMessage
          message="Не удалось загрузить заказы"
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && filteredOrders.length === 0 && (
        <EmptyState
          icon={<Package className="w-8 h-8" />}
          title={search ? 'Ничего не найдено' : 'У вас пока нет заказов'}
          description={search ? 'Попробуйте изменить поисковый запрос' : 'Ваши заказы появятся здесь'}
        />
      )}

      {!isLoading && !isError && filteredOrders.length > 0 && (
        <>
          <div data-tour="customer:orders-list" className="grid gap-3">
            {filteredOrders.map(order => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            className="mt-6"
          />
        </>
      )}
    </div>
  );
};

export default MyOrdersPage;
